import { QueryClient } from "@tanstack/react-query";

const MAX_QUERY_RETRIES = 2;

function httpStatusOf(error: unknown): number | undefined {
  if (typeof error === "object" && error !== null && "status" in error) {
    return typeof error.status === "number" ? error.status : undefined;
  }
  return undefined;
}

/**
 * Retry reads only when retrying can help: network failures and 5xx/429.
 * 4xx (validation, auth, permission, not found) will fail the same way every time.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  const status = httpStatusOf(error);
  if (status !== undefined && status >= 400 && status < 500 && status !== 429) return false;
  return failureCount < MAX_QUERY_RETRIES;
}

/**
 * Query client (WEB-005).
 * Mutations NEVER retry automatically: a timed-out write may have succeeded on the server,
 * and replaying it could duplicate clinical data. Idempotent retries are a per-mutation
 * decision made with an idempotency key (planned with the API client, WEB-009).
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetryQuery,
        staleTime: 30_000,
        refetchOnWindowFocus: true,
      },
      mutations: { retry: false },
    },
  });
}
